<div class="client-pages-page" style="padding: 120px 40px 100px;">

  <!-- Step 1 of the access chain -->
  <app-policy-flow-steps active="pages"></app-policy-flow-steps>

  <!-- Header & Action Bar -->
  <div class="contests-header mb-4">
    <div>
      <h2 class="page-title">{{ 'client-pages.title' | translate }}</h2>
      <p class="page-subtitle text-muted mb-0">{{ 'client-pages.subtitle' | translate }}</p>
    </div>

    <div class="top-filters">
      <div class="search-bar">
        <div class="icon">
          <i class="fa-solid fa-magnifying-glass"></i>
        </div>
        <input type="text" [(ngModel)]="searchTerm" [placeholder]="'client-pages.search-placeholder' | translate" />
      </div>

      <button class="btn btn-light" (click)="loadPages()" [disabled]="isLoading" [title]="'client-pages.refresh' | translate">
        <i class="fa-solid fa-rotate" [class.fa-spin]="isLoading"></i>
      </button>

      <button class="btn btn-outline" *ngIf="missingDefaultPages.length > 0" (click)="openImportModal()" [disabled]="isLoading">
        <i class="fa-solid fa-file-import me-2"></i>
        <span>{{ 'client-pages.import-defaults' | translate }} ({{ missingDefaultPages.length }})</span>
      </button>

      <button class="btn btn-primary" (click)="openCreateModal()">
        <i class="fa-solid fa-plus me-2"></i>
        <span>{{ 'client-pages.new-page' | translate }}</span>
      </button>
    </div>
  </div>

  <!-- Loading -->
  <div *ngIf="isLoading" class="text-center py-5">
    <div class="spinner-border text-primary" role="status" style="color: #5304FB !important;">
      <span class="visually-hidden">Loading...</span>
    </div>
    <div class="mt-2 text-muted small">{{ 'client-pages.loading' | translate }}</div>
  </div>

  <!-- Table -->
  <div *ngIf="!isLoading" class="luqta-card p-0 overflow-hidden border">
    <div class="table-responsive">
      <table class="table table-hover align-middle mb-0 pages-table">
        <thead>
          <tr class="text-muted small">
            <th class="ps-4 py-3" style="width: 7%;">ID</th>
            <th class="py-3" style="width: 27%;">
              <div class="d-flex align-items-center gap-2">
                <span>{{ 'client-pages.col-name' | translate }}</span>
                <button *ngIf="hasAnyChildren" class="tree-toggle-all" (click)="toggleAll()"
                  [title]="(allCollapsed ? 'client-pages.expand-all' : 'client-pages.collapse-all') | translate">
                  <i class="fa-solid" [class.fa-angles-down]="allCollapsed" [class.fa-angles-up]="!allCollapsed"></i>
                </button>
              </div>
            </th>
            <th class="py-3" style="width: 18%;">{{ 'client-pages.col-key' | translate }}</th>
            <th class="py-3" style="width: 23%;">{{ 'client-pages.col-url' | translate }}</th>
            <th class="py-3" style="width: 16%;">{{ 'client-pages.col-hierarchy' | translate }}</th>
            <th class="text-end pe-4 py-3" style="width: 9%;">{{ 'client-pages.col-actions' | translate }}</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let row of displayRows"
              [class.child-row]="row.level === 1"
              [class.parent-row]="row.childCount > 0">
            <td class="ps-4 py-3">
              <span class="id-badge" [class.child]="row.level === 1">#{{ row.page.id }}</span>
            </td>

            <!-- Page name with tree indentation -->
            <td class="py-3">
              <div class="tree-cell" [class.indented]="row.level === 1">
                <!-- elbow connector for a child -->
                <span *ngIf="row.level === 1" class="tree-branch" [class.last]="row.isLastChild"></span>

                <!-- collapse toggle for a parent, spacer otherwise -->
                <button *ngIf="row.childCount > 0" class="tree-chevron" (click)="toggleCollapse(row.page.id, $event)"
                  [title]="(isCollapsed(row.page.id) ? 'client-pages.expand' : 'client-pages.collapse') | translate">
                  <i class="fa-solid fa-chevron-down" [class.collapsed]="isCollapsed(row.page.id)"></i>
                </button>
                <span *ngIf="row.childCount === 0" class="tree-chevron-spacer"></span>

                <div class="page-avatar" [class.child]="row.level === 1">
                  <i [class]="row.level === 1 ? 'fa-solid fa-file-lines' : 'fa-solid fa-folder-open'"></i>
                </div>
                <span class="page-name" [class.child]="row.level === 1">{{ row.page.page_name }}</span>
              </div>
            </td>

            <td class="py-3">
              <code class="key-chip">{{ row.page.page_key }}</code>
            </td>
            <td class="py-3">
              <span class="url-text">{{ row.page.url }}</span>
            </td>

            <!-- Hierarchy: "under X" for a child, "N sub-pages" for a parent -->
            <td class="py-3">
              <span *ngIf="row.level === 1" class="parent-pill">
                <i class="fa-solid fa-turn-up fa-rotate-90"></i>
                {{ 'client-pages.under' | translate }} {{ getParentName(row.page.parent_id) }}
              </span>
              <span *ngIf="row.level === 0 && row.childCount > 0" class="children-pill"
                (click)="toggleCollapse(row.page.id, $event)">
                <i class="fa-solid fa-sitemap"></i>
                {{ row.childCount }}
                {{ (row.childCount === 1 ? 'client-pages.sub-page' : 'client-pages.sub-pages') | translate }}
              </span>
              <span *ngIf="row.level === 0 && row.childCount === 0" class="text-muted small">-</span>
            </td>

            <td class="text-end pe-4 py-3">
              <div class="d-flex align-items-center justify-content-end gap-2">
                <button class="btn-icon-action" (click)="openEditModal(row.page, $event)" [title]="'client-pages.edit' | translate">
                  <i class="fa-solid fa-pen-to-square"></i>
                </button>
                <button class="btn-icon-action btn-icon-danger" (click)="openDeleteModal(row.page, $event)" [title]="'client-pages.delete' | translate">
                  <i class="fa-solid fa-trash-can"></i>
                </button>
              </div>
            </td>
          </tr>

          <!-- Empty -->
          <tr *ngIf="displayRows.length === 0">
            <td colspan="6" class="text-center py-5">
              <i class="fa-solid fa-file-circle-plus text-muted mb-3 d-block" style="font-size: 42px;"></i>
              <h5 class="font-weight-600">{{ 'client-pages.empty-title' | translate }}</h5>
              <p class="text-muted small mb-4">
                {{ (searchTerm ? 'client-pages.empty-search' : 'client-pages.empty-desc') | translate }}
              </p>
              <button *ngIf="!searchTerm" class="btn btn-primary" (click)="openCreateModal()">
                <i class="fa-solid fa-plus me-2"></i> {{ 'client-pages.new-page' | translate }}
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>

  <!-- Create / Edit Modal -->
  <nz-modal
    nzClassName="luqta-custom-modal"
    [nzCentered]="true"
    [(nzVisible)]="isFormModalVisible"
    [nzTitle]="formModalTitle"
    [nzContent]="formModalContent"
    [nzFooter]="formModalFooter"
    (nzOnCancel)="closeFormModal()"
    [nzWidth]="560"
  >
    <ng-template #formModalTitle>
      <div class="d-flex align-items-center gap-2">
        <div class="modal-role-icon" style="background: rgba(255, 255, 255, 0.2); color: #FFFFFF; width: 34px; height: 34px; border-radius: 8px; display: flex; align-items: center; justify-content: center; font-size: 14px;">
          <i [class]="isEditMode ? 'fa-solid fa-pen-to-square' : 'fa-solid fa-plus'"></i>
        </div>
        <span style="font-family: 'Codec Pro'; font-weight: 600; font-size: 17px; color: #FFFFFF;">
          <ng-container *ngIf="isEditMode">{{ 'client-pages.edit-page' | translate }} #{{ form.id }}</ng-container>
          <ng-container *ngIf="!isEditMode">{{ 'client-pages.create-page' | translate }}</ng-container>
        </span>
      </div>
    </ng-template>

    <ng-template #formModalContent>
      <div class="page-form py-3 px-1">

        <!-- Page Name -->
        <div class="mb-3">
          <label class="form-label font-weight-600 mb-2">
            {{ 'client-pages.field-name' | translate }} <span class="text-danger">*</span>
          </label>
          <input type="text" class="form-control luqta-input" [(ngModel)]="form.page_name" (ngModelChange)="onPageNameInput()"
            placeholder="e.g. Contests, Participants, Web QR" [class.is-invalid]="!!formErrors.page_name" />
          <div *ngIf="formErrors.page_name" class="field-error"><i class="fa-solid fa-circle-exclamation"></i>{{ formErrors.page_name }}</div>
        </div>

        <!-- Page Key -->
        <div class="mb-3">
          <label class="form-label font-weight-600 mb-2">
            {{ 'client-pages.field-key' | translate }} <span class="text-danger">*</span>
          </label>
          <input type="text" class="form-control luqta-input mono" [(ngModel)]="form.page_key" (ngModelChange)="onPageKeyInput()"
            placeholder="e.g. contests, web_qr" [class.is-invalid]="!!formErrors.page_key" />
          <div *ngIf="formErrors.page_key" class="field-error"><i class="fa-solid fa-circle-exclamation"></i>{{ formErrors.page_key }}</div>
          <small class="text-muted d-flex align-items-center gap-1 mt-1" style="font-size: 12px;">
            <i class="fa-solid fa-circle-info" style="color: #5304FB;"></i>
            {{ 'client-pages.key-hint' | translate }}
          </small>
        </div>

        <!-- URL -->
        <div class="mb-3">
          <label class="form-label font-weight-600 mb-2">
            {{ 'client-pages.field-url' | translate }} <span class="text-danger">*</span>
          </label>
          <input type="text" class="form-control luqta-input mono" [(ngModel)]="form.url" (ngModelChange)="onFieldInput('url')"
            placeholder="/home/contests" [class.is-invalid]="!!formErrors.url" />
          <div *ngIf="formErrors.url" class="field-error"><i class="fa-solid fa-circle-exclamation"></i>{{ formErrors.url }}</div>
        </div>

        <!-- Parent -->
        <div class="mb-2">
          <label class="form-label font-weight-600 mb-2">{{ 'client-pages.field-parent' | translate }}</label>
          <select class="form-select luqta-input" [(ngModel)]="form.parent_id">
            <option [ngValue]="0">{{ 'client-pages.no-parent' | translate }}</option>
            <option *ngFor="let p of parentOptions" [ngValue]="p.id">{{ p.page_name }} (#{{ p.id }})</option>
          </select>
        </div>

        <!-- Server error -->
        <div *ngIf="serverError" class="alert alert-danger d-flex align-items-start gap-2 p-3 mt-3 mb-0"
          style="border-radius: 10px; background: #FEF2F2; border: 1px solid #FCA5A5; color: #B91C1C; font-size: 13px;">
          <i class="fa-solid fa-triangle-exclamation mt-0.5"></i>
          <div>{{ serverError }}</div>
        </div>
      </div>
    </ng-template>

    <ng-template #formModalFooter>
      <div class="d-flex justify-content-end align-items-center gap-2">
        <button class="btn btn-light"
          style="border-radius: 12px; height: 46px; padding: 0 22px; font-size: 14px; font-weight: 500; border: 1.5px solid rgba(0, 0, 0, 0.1); background: #FFFFFF; color: #344054;"
          (click)="closeFormModal()" [disabled]="isSaving">
          {{ 'client-pages.cancel' | translate }}
        </button>
        <button class="btn btn-primary"
          style="border-radius: 12px; height: 46px; padding: 0 24px; font-size: 14px; font-weight: 600; background: #5304FB; border: none; color: #FFFFFF; box-shadow: 0 2px 8px rgba(83, 4, 251, 0.25);"
          (click)="handleSave()" [disabled]="isSaving || !isFormValid">
          <span *ngIf="isSaving" class="spinner-border spinner-border-sm me-2"></span>
          <i *ngIf="!isSaving && isEditMode" class="fa-solid fa-check me-2"></i>
          <i *ngIf="!isSaving && !isEditMode" class="fa-solid fa-plus me-2"></i>
          <ng-container *ngIf="isSaving">{{ 'client-pages.saving' | translate }}</ng-container>
          <ng-container *ngIf="!isSaving && isEditMode">{{ 'client-pages.save-changes' | translate }}</ng-container>
          <ng-container *ngIf="!isSaving && !isEditMode">{{ 'client-pages.create-page' | translate }}</ng-container>
        </button>
      </div>
    </ng-template>
  </nz-modal>

  <!-- Import Defaults Modal -->
  <nz-modal
    nzClassName="luqta-custom-modal"
    [nzCentered]="true"
    [(nzVisible)]="isImportModalVisible"
    [nzTitle]="importModalTitle"
    [nzContent]="importModalContent"
    [nzFooter]="importModalFooter"
    [nzClosable]="!isImporting"
    [nzMaskClosable]="!isImporting"
    (nzOnCancel)="closeImportModal()"
    [nzWidth]="560"
  >
    <ng-template #importModalTitle>
      <div class="d-flex align-items-center gap-2">
        <div class="modal-role-icon" style="background: rgba(255, 255, 255, 0.2); color: #FFFFFF; width: 34px; height: 34px; border-radius: 8px; display: flex; align-items: center; justify-content: center; font-size: 14px;">
          <i class="fa-solid fa-file-import"></i>
        </div>
        <span style="font-family: 'Codec Pro'; font-weight: 600; font-size: 17px; color: #FFFFFF;">
          {{ 'client-pages.import-defaults' | translate }}
        </span>
      </div>
    </ng-template>

    <ng-template #importModalContent>
      <div class="py-3 px-1">
        <p style="font-size: 14px; color: #344054; line-height: 1.5;" class="mb-3">
          {{ 'client-pages.import-desc' | translate }}
        </p>
        <div class="import-list">
          <div *ngFor="let p of missingDefaultPages" class="import-item">
            <i class="fa-solid" [class.fa-folder-open]="!p.parent_id" [class.fa-file-lines]="!!p.parent_id" [class.child]="!!p.parent_id"></i>
            <span class="name">{{ p.page_name }}</span>
            <code class="key-chip">{{ p.page_key }}</code>
            <span class="url">{{ p.url }}</span>
          </div>
        </div>
        <div *ngIf="isImporting" class="d-flex align-items-center gap-2 mt-3" style="font-size: 13px; color: #5304FB;">
          <span class="spinner-border spinner-border-sm"></span>
          <span>{{ importProgress }}</span>
        </div>
      </div>
    </ng-template>

    <ng-template #importModalFooter>
      <div class="d-flex justify-content-end align-items-center gap-2">
        <button class="btn btn-light"
          style="border-radius: 12px; height: 46px; padding: 0 22px; font-size: 14px; font-weight: 500; border: 1.5px solid rgba(0, 0, 0, 0.1); background: #FFFFFF; color: #344054;"
          (click)="closeImportModal()" [disabled]="isImporting">
          {{ 'client-pages.cancel' | translate }}
        </button>
        <button class="btn btn-primary"
          style="border-radius: 12px; height: 46px; padding: 0 24px; font-size: 14px; font-weight: 600; background: #5304FB; border: none; color: #FFFFFF; box-shadow: 0 2px 8px rgba(83, 4, 251, 0.25);"
          (click)="handleImportDefaults()" [disabled]="isImporting || missingDefaultPages.length === 0">
          <span *ngIf="isImporting" class="spinner-border spinner-border-sm me-2"></span>
          <i *ngIf="!isImporting" class="fa-solid fa-file-import me-2"></i>
          <ng-container *ngIf="isImporting">{{ 'client-pages.importing' | translate }}</ng-container>
          <ng-container *ngIf="!isImporting">{{ 'client-pages.import-now' | translate }} ({{ missingDefaultPages.length }})</ng-container>
        </button>
      </div>
    </ng-template>
  </nz-modal>

  <!-- Delete Modal -->
  <nz-modal
    nzClassName="luqta-custom-modal"
    [nzCentered]="true"
    [(nzVisible)]="isDeleteModalVisible"
    [nzTitle]="deleteModalTitle"
    [nzContent]="deleteModalContent"
    [nzFooter]="deleteModalFooter"
    (nzOnCancel)="closeDeleteModal()"
    [nzWidth]="480"
  >
    <ng-template #deleteModalTitle>
      <div class="d-flex align-items-center gap-2">
        <div class="modal-role-icon" style="background: rgba(239, 68, 68, 0.2); color: #EF4444; width: 34px; height: 34px; border-radius: 8px; display: flex; align-items: center; justify-content: center; font-size: 14px;">
          <i class="fa-solid fa-triangle-exclamation"></i>
        </div>
        <span style="font-family: 'Codec Pro'; font-weight: 600; font-size: 17px; color: #101828;">
          {{ 'client-pages.delete-page' | translate }}
        </span>
      </div>
    </ng-template>

    <ng-template #deleteModalContent>
      <div class="py-3 px-1">
        <p style="font-size: 14.5px; color: #344054; line-height: 1.5;">
          {{ 'client-pages.delete-confirm' | translate }} <strong>"{{ deletingPage?.page_name }}"</strong> (ID #{{ deletingPage?.id }})?
        </p>
        <div *ngIf="deletingChildrenCount > 0" class="alert alert-warning d-flex align-items-start gap-2 p-3 mt-3 mb-0"
          style="border-radius: 10px; background: #FFFBEB; border: 1px solid #FDE68A; color: #B45309; font-size: 13px;">
          <i class="fa-solid fa-triangle-exclamation mt-0.5"></i>
          <div>
            <strong>{{ 'client-pages.warning' | translate }}:</strong>
            {{ deletingChildrenCount }} {{ 'client-pages.children-warning' | translate }}
          </div>
        </div>
      </div>
    </ng-template>

    <ng-template #deleteModalFooter>
      <div class="d-flex justify-content-end align-items-center gap-2">
        <button class="btn btn-light"
          style="border-radius: 12px; height: 44px; padding: 0 20px; font-size: 14px; font-weight: 500; border: 1.5px solid rgba(0, 0, 0, 0.1); background: #FFFFFF; color: #344054;"
          (click)="closeDeleteModal()" [disabled]="isDeleting">
          {{ 'client-pages.cancel' | translate }}
        </button>
        <button class="btn btn-danger"
          style="border-radius: 12px; height: 44px; padding: 0 22px; font-size: 14px; font-weight: 600; background: #EF4444; border: none; color: #FFFFFF;"
          (click)="handleDelete()" [disabled]="isDeleting">
          <span *ngIf="isDeleting" class="spinner-border spinner-border-sm me-2"></span>
          <i *ngIf="!isDeleting" class="fa-solid fa-trash-can me-2"></i>
          <ng-container *ngIf="isDeleting">{{ 'client-pages.deleting' | translate }}</ng-container>
          <ng-container *ngIf="!isDeleting">{{ 'client-pages.delete-page' | translate }}</ng-container>
        </button>
      </div>
    </ng-template>
  </nz-modal>

</div>
